import { APP_EK } from 'configs';
import * as CryptoJS from './crypto';
import { GenericObject, StorageData, ExpiryData } from './types';
import { Environment } from 'configs/constants';

export const SKey = APP_EK || 'abc';
const isLocalMode = import.meta.env.VITE_APP_MODE === Environment.LOCAL;

/**
 * @description Save data into browser localStorage
 * @param {String} key
 * @param {StorageData} data
 * @param {boolean} isJson
 * @param {boolean} isEncrypted
 * @define key: Key name for localStorage
 * @define data: any kind of data to store in localStorage
 * @define isJson: if data is Json then it should if true else false
 * @define isEncrypted: if data needs to be encrypted then mark as true
 * @return {void}
 */
export const saveToLocal = (key: string, data: StorageData, isJson = true, isEncrypted = true): void => {
  let saveData: string = data as string;

  if (data && isJson) {
    saveData = JSON.stringify(data);
  }
  if (saveData && isEncrypted && !isLocalMode) {
    saveData = CryptoJS.AES.encrypt(saveData, SKey).toString();
  }

  localStorage.setItem(key, saveData);
};

/**
 * @description Get data from browser localStorage
 * @param {String} key
 * @param {boolean} isJson
 * @param {boolean} isEncrypted
 * @define key: Key name for localStorage
 * @define isJson: if data is Json then it should if true else false
 * @define isEncrypted: if data is encrypted then mark as true
 * @return {StorageData | null}
 */
export const getFromLocal = (key: string, isJson = true, isEncrypted = true): StorageData | null => {
  let data = localStorage.getItem(key);

  if (data && isEncrypted && !isLocalMode) {
    const bytes = CryptoJS.AES.decrypt(data.toString(), SKey);
    data = bytes.toString(CryptoJS.encUTF8);
  }
  if (data && isJson) {
    data = JSON.parse(data);
  }
  return data;
};

/**
 * @description Remove data from browser localStorage
 * @param {String} key
 * @define key: Key name for localStorage
 * @return {void}
 */
export const removeFromLocal = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * @description check data exists in browser localStorage
 * @param {String} key
 * @define key: Key name for localStorage
 * @return {boolean}
 */
export const existInLocal = (key: string): boolean => localStorage.getItem(key) != null;

/**
 * @description Save data into localStorage with Expiry
 * @param {String} key
 * @param {T} data
 * @param {Date} expiry
 * @define key: Key name for localStorage
 * @define data: any kind of data to store in localStorage
 * @define expiry: Date time to set the expiry
 * @return {void}
 */
export const saveInLocalWithExpiry = <T extends GenericObject>(key: string, data: T, expiry: Date): void => {
  const dataToSave: ExpiryData<T> = {
    data,
    expiresAt: expiry,
  };
  saveToLocal(key, dataToSave);
};

/**
 * @description Save data into browser sessionStorage
 * @param {String} key
 * @param {StorageData} data
 * @param {boolean} isJson
 * @param {boolean} isEncrypted
 * @define key: Key name for sessionStorage
 * @define data: any kind of data to store in sessionStorage
 * @define isJson: if data is Json then it should if true else false
 * @define isEncrypted: if data needs to be encrypted then mark as true
 * @return {void}
 */
export const saveToSession = (key: string, data: StorageData, isJson = true, isEncrypted = true): void => {
  let saveData: string = data as string;

  if (data && isJson) {
    saveData = JSON.stringify(data);
  }
  if (saveData && isEncrypted && !isLocalMode) {
    saveData = CryptoJS.AES.encrypt(saveData, SKey).toString();
  }
  sessionStorage.setItem(key, saveData);
};

/**
 * @description Get data from browser sessionStorage
 * @param {String} key
 * @param {boolean} isJson
 * @param {boolean} isEncrypted
 * @define key: Key name for sessionStorage
 * @define isJson: if data is Json then it should if true else false
 * @define isEncrypted: if data is encrypted then mark as true
 * @return {StorageData | null}
 */
export const getFromSession = (key: string, isJson = true, isEncrypted = true): StorageData | null => {
  let data = sessionStorage.getItem(key);

  if (data && isEncrypted && !isLocalMode) {
    const bytes = CryptoJS.AES.decrypt(data.toString(), SKey);
    data = bytes.toString(CryptoJS.encUTF8);
  }
  if (data && isJson) {
    data = JSON.parse(data);
  }
  return data;
};

/**
 * @description Remove data from browser sessionStorage
 * @param {String} key
 * @define key: Key name for sessionStorage
 * @return {void}
 */
export const removeFromSession = (key: string): void => {
  sessionStorage.removeItem(key);
};

/**
 * @description check data exists in browser sessionStorage
 * @param {String} key
 * @define key: Key name for sessionStorage
 * @return {boolean}
 */
export const existInSession = (key: string): boolean => sessionStorage.getItem(key) != null;

/**
 * @description get data from localStorage with Expiry boolean
 * @param {String} key
 * @define key: Key name for localStorage
 * @return {object}
 */
/* export const getFromLocalWithExpiry = (key: string): any => {
  const savedData: any = getFromLocal(key);
  let response: any = null;

  if (savedData) {
    const { expiresAt } = savedData;

    // if current time is after the saved expiry
    const isExpired = moment().isAfter(expiresAt);

    response = {
      isExpired,
      data: savedData.data,
    };
  }

  return response;
};
 */
